import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Filter, Download, TrendingUp, Users, BookOpen, Music } from 'lucide-react';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  fetchRecentActivities,
  fetchActivityAnalytics,
  fetchEngagementTrends,
  exportActivityData
} from '../../services/activityFeedService';
import ActivityListItem from './components/ActivityListItem';
import AnalyticsCard from './components/AnalyticsCard';
import EngagementChart from './components/EngagementChart';
import FilterControls from './components/FilterControls';

const RealTimeActivityFeedDashboard = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    actionType: 'all',
    userType: 'all',
    timeRange: 168, // 7 days
    limit: 50
  });

  // Fetch all data
  const loadActivityData = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Fetch activities, analytics, and trends in parallel
      const [activitiesData, analyticsData, trendsData] = await Promise.all([
        fetchRecentActivities(filters),
        fetchActivityAnalytics(filters?.timeRange),
        fetchEngagementTrends(7)
      ]);

      setActivities(activitiesData);
      setAnalytics(analyticsData);
      setTrends(trendsData);

    } catch (error) {
      console.error('Error loading activity data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadActivityData();
  }, []);

  // Reload when filters change
  useEffect(() => {
    if (!loading) {
      loadActivityData(true);
    }
  }, [filters]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadActivityData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Handle export
  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await exportActivityData(filters);
      
      // Create download link
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-feed-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      window.URL?.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting activity data:', error);
      alert('Failed to export activity data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Handle activity click
  const handleActivityClick = (activity) => {
    if (activity?.navigateTo) {
      navigate(activity?.navigateTo);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Real-Time Activity Feed
            </h1>
            <p className="text-white/80">
              Monitor platform engagement and user activities across RoleCall
            </p>
          </div>
          
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-6 py-3 bg-white text-[#6e32b9] rounded-lg font-semibold hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={20} />
            {exporting ? 'Exporting...' : 'Export Data'}
          </button>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <AnalyticsCard
            icon={Activity}
            title="Total Activities"
            value={analytics?.totalActivities || 0}
            subtitle={`Last ${Math.round(filters?.timeRange / 24)} days`}
            color="bg-blue-500"
          />
          <AnalyticsCard
            icon={Music}
            title="Song Additions"
            value={analytics?.songAdditions || 0}
            subtitle="New songs added"
            color="bg-purple-500"
          />
          <AnalyticsCard
            icon={BookOpen}
            title="Course Completions"
            value={analytics?.courseCompletions || 0}
            subtitle="Lessons finished"
            color="bg-green-500"
          />
          <AnalyticsCard
            icon={Users}
            title="Active Users"
            value={analytics?.activeUsers || 0}
            subtitle="Users with activity"
            color="bg-orange-500"
          />
        </div>

        {/* Engagement Trends Chart */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="text-white" size={24} />
            <h2 className="text-2xl font-bold text-white">
              Engagement Trends
            </h2>
          </div>
          <EngagementChart data={trends} />
        </div>

        {/* Filter Controls */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6">
          <FilterControls
            filters={filters}
            onFilterChange={handleFilterChange}
            activityCount={activities?.length}
            refreshing={refreshing}
          />
        </div>

        {/* Activity Stream */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">
              Activity Stream
              {refreshing && (
                <span className="ml-3 text-sm font-normal text-white/60">
                  Refreshing...
                </span>
              )}
            </h2>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span>Live Updates</span>
            </div>
          </div>

          {activities?.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="mx-auto mb-4 text-white/40" size={48} />
              <p className="text-white/60 text-lg">
                No activities found for the selected filters
              </p>
              <p className="text-white/40 text-sm mt-2">
                Try adjusting your filter settings
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities?.map((activity) => (
                <ActivityListItem
                  key={activity?.id}
                  activity={activity}
                  onClick={() => handleActivityClick(activity)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Auto-refresh indicator */}
        <div className="mt-6 text-center text-white/60 text-sm">
          <p>Dashboard refreshes automatically every 30 seconds</p>
        </div>
      </div>
    </div>
  );
};

export default RealTimeActivityFeedDashboard;